import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {OrderOptions} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {useEffect} from 'react'
import {getScreenReaderMessage} from '../helpers/screen-reader-helpers'
import type {DashboardTypes} from '@github-ui/shared-dashboard-sidebar/types/dashboard-types'
import {getEmptyListMessages} from '@github-ui/shared-dashboard-lists/shared/helpers/blankslate-helpers'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import {defaultSortingOptions} from '../constants/pulls-sorting'

/**
 * Hook to manage screen reader announcements.
 * Announces search results and empty states to screen reader users.
 * @param dashboardType - The type of dashboard (e.g., issues, pull requests).
 * @param isFetching - Whether a fetch is currently in progress.
 * @param currentPage - The current page number.
 * @param fetchedQuery - The query string that was fetched.
 * @param defaultSortingKey - The default sorting key to use.
 * @param resultCount - The number of results returned from the search.
 * @param pageCount - The total number of pages available for the current query.
 * @param errorMessage - Optional error message to announce when the query is invalid or results in an error.
 * @param resultNoun - Optional singular/plural noun override for the result count.
 * @param sortingOptions - Optional sorting options used to resolve the sort label in the announcement.
 *   Pass the same options supplied to the visual dropdown (e.g. issue-specific options that include
 *   reaction sub-options) so the announced label matches what is displayed.
 * @returns An object containing the emptyListState for display.
 */
export function useScreenReaderAnnouncements(
  dashboardType: DashboardTypes,
  isFetching: boolean,
  currentPage: number,
  fetchedQuery: string,
  defaultSortingKey: string,
  resultCount: number,
  pageCount: number,
  errorMessage?: string,
  resultNoun?: {singular: string; plural: string},
  sortingOptions: Record<string, OrderOptions> = defaultSortingOptions,
) {
  const emptyListState: EmptyListState = {
    dashboardType,
    filtered: !!fetchedQuery,
    currentPage,
    pageCount,
    errorMessage,
  }

  const emptyMessages = getEmptyListMessages(emptyListState)

  const message = getScreenReaderMessage(
    dashboardType,
    sortingOptions,
    defaultSortingKey,
    () => emptyMessages,
    isFetching,
    currentPage,
    fetchedQuery,
    resultCount,
    pageCount,
    resultNoun,
  )

  useEffect(() => {
    const debouncedAnnounce = debounce(announce, 300)

    if (message) debouncedAnnounce(message)

    return () => {
      debouncedAnnounce.cancel()
    }
  }, [message])

  return {emptyListState}
}
